import { useState, useRef, useCallback } from 'react';
import type { ReviewPhotoUploadStates } from '../../hooks/useReviewPhotoUpload';
import { createTempPhotoId, getFileKey } from './reviewPhotoIds';
import { FormSection } from '../ui/FormSection';

export interface ReviewPhoto {
  id: string;
  url: string;
  file?: File;
  /** Identity of the staged file, used to reject duplicate adds. */
  fileKey?: string;
}

export interface ReviewPhotoUploadProps {
  photos: ReviewPhoto[];
  onPhotosChange: (photos: ReviewPhoto[]) => void;
  maxPhotos?: number;
  disabled?: boolean;
  className?: string;
  error?: string;
  /** Per-photo upload progress, keyed by photo ID, supplied while the review is submitting. */
  uploadStates?: ReviewPhotoUploadStates;
}

export const ReviewPhotoUpload = ({
  photos,
  onPhotosChange,
  maxPhotos = 4,
  disabled = false,
  className = '',
  error,
  uploadStates = {},
}: ReviewPhotoUploadProps) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const [notice, setNotice] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const canAddMore = photos.length < maxPhotos;

  const handleFiles = useCallback(
    (files: FileList) => {
      const stagedKeys = new Set(photos.map((photo) => photo.fileKey).filter(Boolean));
      const messages: string[] = [];

      const uniqueFiles = Array.from(files).filter((file) => {
        const key = getFileKey(file);
        if (stagedKeys.has(key)) {
          messages.push(`${file.name} has already been added.`);
          return false;
        }
        // Guards against the same file appearing twice in a single drop.
        stagedKeys.add(key);
        return true;
      });

      const remainingSlots = maxPhotos - photos.length;
      const filesToAdd = uniqueFiles.slice(0, remainingSlots);

      if (uniqueFiles.length > remainingSlots) {
        messages.push(`You can attach up to ${maxPhotos} photos, so some were not added.`);
      }

      setNotice(messages.join(' '));

      if (filesToAdd.length === 0) return;

      const newPhotos: ReviewPhoto[] = filesToAdd.map((file) => ({
        id: createTempPhotoId(),
        url: URL.createObjectURL(file),
        file,
        fileKey: getFileKey(file),
      }));

      onPhotosChange([...photos, ...newPhotos]);
    },
    [photos, maxPhotos, onPhotosChange]
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragOver(false);

      if (disabled || !canAddMore) return;

      const files = e.dataTransfer.files;
      if (files.length > 0) {
        handleFiles(files);
      }
    },
    [disabled, canAddMore, handleFiles]
  );

  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (files && files.length > 0) {
        handleFiles(files);
      }

      // Reset input value to allow uploading the same file again
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    },
    [handleFiles]
  );

  const handleClick = useCallback(() => {
    if (!disabled && canAddMore) {
      fileInputRef.current?.click();
    }
  }, [disabled, canAddMore]);

  const handleRemovePhoto = useCallback(
    (photoId: string) => {
      const photoToRemove = photos.find((p) => p.id === photoId);
      if (photoToRemove?.url.startsWith('blob:')) {
        URL.revokeObjectURL(photoToRemove.url);
      }
      setNotice('');
      onPhotosChange(photos.filter((p) => p.id !== photoId));
    },
    [photos, onPhotosChange]
  );

  return (
    <FormSection
      title="Photos"
      titleAs="h3"
      description="Optional."
      className={className}
      contentClassName=""
      actions={
        <span className="text-xs text-gray-500">
          {photos.length}/{maxPhotos} photos
        </span>
      }
    >
      {/* Photo Grid */}
      {photos.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
          {photos.map((photo) => {
            const uploadState = uploadStates[photo.id];
            const isUploading = uploadState?.status === 'uploading';
            const hasError = uploadState?.status === 'error';

            return (
              <div key={photo.id} className="relative aspect-square group">
                <img
                  src={photo.url}
                  alt="Review photo"
                  className={`
                    w-full h-full object-cover rounded-lg
                    ${isUploading ? 'opacity-50' : ''}
                    ${hasError ? 'border-2 border-red-500' : ''}
                  `}
                />
                {isUploading && (
                  <div
                    className="absolute inset-0 flex flex-col items-center justify-center gap-2 px-2"
                    role="status"
                    aria-live="polite"
                  >
                    <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
                    <div className="w-full bg-white/70 rounded-full h-1.5 overflow-hidden">
                      <div
                        className="bg-primary-600 h-full rounded-full transition-all duration-200"
                        style={{ width: `${uploadState.progress}%` }}
                      />
                    </div>
                    <span className="text-xs font-medium text-gray-800">
                      Uploading {uploadState.progress}%
                    </span>
                  </div>
                )}
                {!isUploading && !disabled && (
                  <button
                    type="button"
                    onClick={() => handleRemovePhoto(photo.id)}
                    className="
                      absolute top-1 right-1 p-1
                      bg-red-500 text-white rounded-full
                      opacity-0 group-hover:opacity-100
                      transition-opacity duration-200
                      hover:bg-red-600
                      focus:outline-none focus:ring-2 focus:ring-info-500 focus:ring-offset-1
                    "
                    aria-label="Remove photo"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                )}
                {hasError && (
                  <div className="absolute bottom-0 left-0 right-0 bg-red-500 text-white text-xs p-1 text-center">
                    Upload failed
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Upload Area */}
      {canAddMore && (
        <div
          className={`
            relative border-2 border-dashed rounded-lg p-4 text-center transition-colors
            ${isDragOver ? 'border-primary-500 bg-primary-50' : 'border-gray-300'}
            ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:border-gray-400 hover:bg-gray-50'}
          `}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={handleClick}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            onChange={handleFileInputChange}
            className="hidden"
            disabled={disabled}
          />

          <div className="space-y-1">
            <svg
              className="w-8 h-8 text-gray-400 mx-auto"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
            <p className="text-sm text-gray-600">
              <span className="font-medium text-primary-600">Click to upload</span> or drag and drop
            </p>
            <p className="text-xs text-gray-500">
              PNG, JPG, GIF up to 10MB ({maxPhotos - photos.length} remaining)
            </p>
          </div>
        </div>
      )}

      {notice && (
        <p className="mt-2 text-sm text-gray-600" role="status">
          {notice}
        </p>
      )}

      {error && (
        <p className="mt-2 text-sm text-red-600" role="alert">
          {error}
        </p>
      )}
    </FormSection>
  );
};
